import React, {
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import classNames from 'classnames';

import { OptionBadge, Preloader } from '../../../common';
import { Icon } from '../../../../Icon';
import useStyles from '../../SelectInput.styles';
import { Group } from '../common';
import { ColorIndicator, SelectInputItem } from '../../types';
import { PopupIcon } from '../../PopupIcon';

import { Option } from './Option';

interface MultipleAutocompleteProps<T extends string | number> {
  items: SelectInputItem<T>[];
  label?: string;
  value: T[] | undefined;
  onChange: (value: T[] | undefined) => void;
  testId?: string;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  clearable?: boolean;
  loading?: boolean;
  error?: boolean;
  helperText?: string;
  renderOption?: (option: SelectInputItem<T>) => ReactNode;
  emptyOption?: React.ReactNode;
  inputValue: string;
  setInputValue: (value: string) => void;
  isThereSomeGroup: boolean;
  inputId: string;
  handlePopoverOpen: () => void;
  valueBadge: OptionBadge | undefined;
  valueColorIndicator: ColorIndicator;
  onFocus: () => void;
}

export const MultipleAutocomplete = <T extends string | number>({
  label,
  value,
  items,
  onChange,
  disabled = false,
  placeholder,
  testId,
  clearable = false,
  loading = false,
  error = false,
  helperText,
  renderOption,
  emptyOption,
  inputValue,
  setInputValue,
  isThereSomeGroup,
  inputId,
  handlePopoverOpen,
  valueBadge,
  valueColorIndicator,
  onFocus,
  ...rest
}: MultipleAutocompleteProps<T>) => {
  const { classes } = useStyles();

  const [selectedValues, setSelectedValues] = useState<SelectInputItem<T>[]>(
    items.filter((opt) => value?.includes(opt.value) ?? []),
  );

  const inputClasses = useMemo(
    () => ({
      paper: classNames(classes.list, classes.autocompleteList, {
        [classes.longList]: !!renderOption,
      }),
    }),
    [classes, renderOption],
  );

  const loadingInputProps = useMemo(() => {
    return loading
      ? {
          slotProps: {
            input: {
              endAdornment: <Preloader />,
            },
          },
        }
      : {};
  }, [loading]);

  const groupBy = useCallback(({ group }: SelectInputItem<T>) => {
    return group || 'Заявки без группировки по коллекциям';
  }, []);

  const groupProps = useMemo(() => {
    return isThereSomeGroup
      ? {
          groupBy,
        }
      : {};
  }, [isThereSomeGroup, groupBy]);

  const handleInputChange = (
    _: SyntheticEvent<Element, Event>,
    nextValue: string,
    reason:
      | 'input'
      | 'reset'
      | 'clear'
      | 'blur'
      | 'selectOption'
      | 'removeOption',
  ) => {
    if (reason === 'input') {
      setInputValue(nextValue);
    }
  };

  const handleChange = (
    _: SyntheticEvent<Element, Event>,
    nextValue: SelectInputItem<T>[],
  ) => {
    setSelectedValues(nextValue);
    onChange(nextValue.map((item) => item.value));
  };

  const handleOnBlur = () => {
    if (selectedValues.length) {
      setInputValue(selectedValues.map((item) => item.label).join(', '));
    } else {
      setInputValue('');
    }
  };

  useEffect(() => {
    setInputValue(
      items
        .filter((item) => value?.includes(item.value))
        .map((item) => item.label)
        .join(', '),
    );
    // eslint-disable-next-line
  }, []);

  return (
    <Autocomplete<SelectInputItem<T>, true, boolean>
      {...groupProps}
      id={inputId}
      multiple
      disableCloseOnSelect
      data-testid={testId}
      classes={inputClasses}
      value={items.filter((option) => value?.includes(option.value))}
      inputValue={loading ? 'Загрузка...' : inputValue}
      options={items}
      disabled={disabled || loading}
      disableClearable={!clearable}
      popupIcon={<PopupIcon mx="12" />}
      clearIcon={
        <Icon name="dismiss_substract" color="stroke.secondary" size="24" />
      }
      noOptionsText={emptyOption || 'Ничего не найдено'}
      renderTags={() => null}
      getOptionDisabled={(option) => !!option.disabled}
      renderGroup={(params) => <Group {...params} />}
      getOptionLabel={(option) => option.label}
      onInputChange={handleInputChange}
      onClose={() => setInputValue('')}
      onChange={handleChange}
      onFocus={onFocus}
      onBlur={handleOnBlur}
      renderInput={(params: React.JSX.IntrinsicAttributes & TextFieldProps) => (
        <TextField
          {...rest}
          {...params}
          {...loadingInputProps}
          id={inputId}
          onClick={handlePopoverOpen}
          classes={{ root: `${classes.inputRoot} aqa_select` }}
          variant="outlined"
          placeholder={placeholder || label}
          error={error}
          helperText={helperText}
        />
      )}
      renderOption={(props, option) => (
        <Option
          value={value}
          inputValue={inputValue}
          renderOption={renderOption}
          optionProps={props}
          option={option}
        />
      )}
    />
  );
};
